import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Minus } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  inventoryService,
  type RecordMovementPayload,
  type StockMovementInput,
} from '@/services/inventory';
import type { InventoryItem } from '@/types/inventory';

/**
 * Target of an adjustment: the item being adjusted plus the specific
 * warehouse + current on-hand for the preview math. The detail sheet
 * builds this by overlaying the selected stock-level row onto the
 * catalog item.
 */
export interface AdjustStockTarget extends InventoryItem {
  warehouseId: string;
  warehouseName: string | null;
  quantityOnHand: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: AdjustStockTarget | null;
}

/**
 * Adjusts stock at a specific warehouse by recording a movement. Operators
 * pick INCREMENT or DECREMENT (as opposed to INBOUND/OUTBOUND, which are
 * routine receipts / shipments). The reason is captured in the audit trail.
 */
export function AdjustStockDialog({ open, onOpenChange, item }: Props) {
  const qc = useQueryClient();
  const [direction, setDirection] = useState<'increment' | 'decrement'>('increment');
  const [quantity, setQuantity] = useState<string>('');
  const [reason, setReason] = useState<string>('');

  useEffect(() => {
    if (open) {
      setDirection('increment');
      setQuantity('');
      setReason('');
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: (payload: RecordMovementPayload) => inventoryService.recordMovement(payload),
    onSuccess: () => {
      toast.success(`Stock adjusted: ${direction === 'increment' ? '+' : '−'}${quantity}`);
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onOpenChange(false);
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!item || !item.warehouseId) return;
    const qty = Number(quantity);
    if (!qty || qty <= 0) return;
    const movementType: StockMovementInput =
      direction === 'increment' ? 'ADJUSTMENT_INCREMENT' : 'ADJUSTMENT_DECREMENT';
    mutation.mutate({
      itemId: item.id,
      warehouseId: item.warehouseId,
      movementType,
      quantity: qty,
      reason: reason.trim() || undefined,
    });
  };

  const onHand = item?.quantityOnHand ?? 0;
  const newOnHand = direction === 'increment' ? onHand + Number(quantity || 0) : onHand - Number(quantity || 0);
  const wouldGoNegative = direction === 'decrement' && newOnHand < 0;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={`Adjust stock${item ? ` · ${item.name}` : ''}`}
      description={
        item
          ? `${item.sku} · ${item.warehouseName ?? item.warehouseId} · currently ${onHand} on hand. Captured in the audit trail.`
          : 'Manual stock correction. Captured in the audit trail.'
      }
      submitLabel={direction === 'increment' ? 'Add stock' : 'Remove stock'}
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="xl"
    >
      <FormField label="Direction" htmlFor="direction" required>
        <div className="grid grid-cols-2 gap-2" id="direction">
          <DirectionButton
            active={direction === 'increment'}
            tone="success"
            icon={Plus}
            label="Add stock"
            sublabel="ADJUSTMENT_INCREMENT"
            onClick={() => setDirection('increment')}
          />
          <DirectionButton
            active={direction === 'decrement'}
            tone="destructive"
            icon={Minus}
            label="Remove stock"
            sublabel="ADJUSTMENT_DECREMENT"
            onClick={() => setDirection('decrement')}
          />
        </div>
      </FormField>

      <FormField
        label={direction === 'increment' ? 'Quantity to add' : 'Quantity to remove'}
        htmlFor="quantity"
        required
        hint={
          quantity && !wouldGoNegative
            ? `New on-hand: ${newOnHand}`
            : wouldGoNegative
              ? `Would go negative — current on-hand is ${onHand}`
              : undefined
        }
        error={wouldGoNegative ? 'Cannot remove more than on hand' : undefined}
      >
        <Input
          id="quantity"
          type="number"
          inputMode="decimal"
          min={0}
          step="0.001"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="0"
          required
        />
      </FormField>

      <FormField
        label="Reason"
        htmlFor="reason"
        hint="Why is this adjustment happening? Captured in the audit trail."
      >
        <Textarea
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Cycle count correction · Damaged unit removed · Found in back room"
          rows={2}
        />
      </FormField>
    </FormSheet>
  );
}

function DirectionButton({
  active,
  tone,
  icon: Icon,
  label,
  sublabel,
  onClick,
}: {
  active: boolean;
  tone: 'success' | 'destructive';
  icon: typeof Plus;
  label: string;
  sublabel: string;
  onClick: () => void;
}) {
  const activeClass =
    tone === 'success'
      ? 'border-success bg-success/10 text-foreground'
      : 'border-destructive bg-destructive/10 text-foreground';
  const iconClass = tone === 'success' ? 'text-success' : 'text-destructive';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-3 text-left transition-colors',
        active ? activeClass : 'border-border bg-card hover:bg-muted/50',
      )}
    >
      <div className="flex items-center gap-2">
        <Icon className={cn('h-4 w-4', active ? iconClass : 'text-muted-foreground')} />
        <span className="text-[13px] font-semibold">{label}</span>
      </div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.05em] text-muted-foreground">
        {sublabel}
      </div>
    </button>
  );
}
